import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    DaysToCompleteDateRangeOptions,
    Metrics,
} from '@core/constants/insights-metrics.constants';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AgingProgressReportRequest,
    ClaimSearchRequest,
    FindByDaysToCompleteRequest,
    LocalDateRange,
} from '@core/generated/openapi';
import { AuthService } from '@services/auth/auth.service';
import dayjs from 'dayjs';
import { FormGroupState } from '@core/constants/form.constants';
import { ClaimService } from '@services/claim/claim.service';
import { GridClientPagination, Option } from 'rcp-common-lib';
import { EnumService } from '@services/enum/enum.service';
import { DateValidators } from '@core/validators/formValidators';
import moment from 'moment';
import { isNil } from 'lodash-es';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';

@Injectable({
    providedIn: 'root',
})
export class AgingInsightsMetricsService {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );
    private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        false,
    );
    pagination: GridClientPagination = {
        enabled: true,
        pageSize: 25,
        pageSizeSelector: [25, 50, 75, 100],
    };
    NO_DATA_MESSAGE: string = 'No results found for search criteria';
    private exportResultsSubject = new Subject<Metrics>();
    exportResults$ = this.exportResultsSubject.asObservable();

    selectedAgingMetric: BehaviorSubject<Metrics> =
        new BehaviorSubject<Metrics>(undefined);

    get isLoading(): BehaviorSubject<boolean> {
        return this._isLoading;
    }

    set isLoading(loading: boolean) {
        this._isLoading.next(loading);
    }

    constructor(
        private authService: AuthService,
        private formBuilder: FormBuilder,
        private claimService: ClaimService,
        private enumService: EnumService,
    ) {}

    search(form: FormGroup, metric: Metrics): Observable<any[]> {
        let request = this.getRequestForMetric(form, metric);

        return this.getEndpointForMetric(metric, request, form);
    }

    getRequestForMetric(form: FormGroup, metric: Metrics): any {
        let request: any;

        switch (metric) {
            case Metrics.AgingProgress:
                const [selectedMonth, selectedYear] =
                    form.controls['monthYear'].value.split('_');
                request = <AgingProgressReportRequest>{
                    monthYear: {
                        year: selectedYear,
                        month: selectedMonth,
                    },
                    payerType: form.controls['payerType'].getRawValue(),
                    claimStatus: form.controls['claimStatus'].getRawValue(),
                    negativeBalance:
                        form.controls['negativeBalance'].getRawValue(),
                    accountLocationIds:
                        this.authService.isAgingUploadByLocationOff(
                            this.authService.agingLocationFilter.value,
                        )
                            ? []
                            : this.authService.agingLocationFilter?.value,
                    nonStalledClaimsOnly:
                        !form.controls['includeStalled'].value,
                };
                break;
            case Metrics.DaysToComplete:
                request = <FindByDaysToCompleteRequest>{
                    accountLocationIds:
                        this.authService.isAgingUploadByLocationOff(
                            this.authService.agingLocationFilter.value,
                        )
                            ? []
                            : this.authService.agingLocationFilter?.value,
                    dateRange: this.getLocalDateRangeFromDateRangeOptions(form),
                    daysToComplete: form.controls['daysToComplete'].value?.map(
                        (item: any) => {
                            if (!isNil(item.value)) {
                                return item.value;
                            } else {
                                return item;
                            }
                        },
                    ),
                    payorIds: form.controls['activePayers'].getRawValue()
                        ? form.controls['activePayers'].getRawValue()
                        : [],
                    nonStalledClaimsOnly:
                        !form.controls['includeStalled'].value,
                };
                break;
            case Metrics.AgingStalledClaims:
                request = <ClaimSearchRequest>{
                    stalledReasonIdList:
                        form.controls['stalledReason'].getRawValue(),
                    stalledReasonIsNull: false,
                    statusList: form.controls['claimStatus'].getRawValue()
                        ? form.controls['claimStatus'].getRawValue()
                        : [],
                    accountPayorIdList: this.getPayerValueForStalledClaims(
                        form.controls['activePayers'].getRawValue(),
                    ),
                    uploadAccountLocationIdIdList:
                        this.authService.isAgingUploadByLocationOff(
                            this.authService.agingLocationFilter.value,
                        )
                            ? []
                            : this.authService.agingLocationFilter?.value,
                };
                break;
        }

        return request;
    }

    getEndpointForMetric(metric: Metrics, request: any, form: FormGroup): any {
        switch (metric) {
            case Metrics.AgingProgress:
                return this.claimService.findAgingReportResults(request);
            case Metrics.DaysToComplete:
                return this.claimService.findAllClaimsByDaysToComplete(request);
            case Metrics.AgingStalledClaims:
                return this.claimService.findAllStalledClaims(request);
            default:
                return undefined;
        }
    }

    getSearchForm(metric: Metrics, savedForm?: FormGroupState) {
        let form: FormGroup;

        switch (metric) {
            case Metrics.AgingProgress:
                form = this.formBuilder.group({
                    monthYear: new FormControl(
                        `${dayjs().month() + 1}_${dayjs().year()}`,
                        {
                            validators: Validators.required,
                        },
                    ),
                    payerType: new FormControl(
                        AgingProgressReportRequest.PayerTypeEnum.AllPayers,
                        { validators: Validators.required },
                    ),
                    claimStatus: new FormControl(
                        AgingProgressReportRequest.ClaimStatusEnum.AllStatuses,
                        { validators: Validators.required },
                    ),
                    negativeBalance: new FormControl(
                        AgingProgressReportRequest.NegativeBalanceEnum.IncludeNegativeBalances,
                        { validators: Validators.required },
                    ),
                    includeStalled: new FormControl(
                        this.claimStalledFilterService.getFilterStatusValue(
                            STALLED_CLAIM_FILTER_IDS.INSIGHTS_AGING_PROGRESS_DETAIL,
                        ),
                    ),
                });
                break;
            case Metrics.DaysToComplete:
                const dayOptions = this.enumService
                    .getOptionsFromEnum(
                        FindByDaysToCompleteRequest.DaysToCompleteEnum,
                    )
                    .filter(
                        (item) =>
                            item.value !==
                            FindByDaysToCompleteRequest.DaysToCompleteEnum.All,
                    )
                    .map((item: Option) => {
                        return item.value;
                    });

                form = this.formBuilder.group(
                    {
                        quarterDateRanges: new FormControl(
                            this.getDaysToCompleteDateRangeOptions()[0].value,
                            {
                                validators: Validators.required,
                            },
                        ),
                        daysToComplete: new FormControl(dayOptions, {
                            validators: Validators.required,
                        }),
                        activePayers: new FormControl(null),
                        startDate: new FormControl(null),
                        endDate: new FormControl(null),
                        includeStalled: new FormControl(
                            this.claimStalledFilterService.getFilterStatusValue(
                                STALLED_CLAIM_FILTER_IDS.INSIGHTS_DAYS_TO_COMPLETE,
                            ),
                        ),
                    },
                    {
                        validators: [
                            DateValidators.dateLessThan(
                                'startDate',
                                'endDate',
                                {
                                    dateRangeValid: true,
                                },
                            ),
                        ],
                    },
                );
                break;
            case Metrics.AgingStalledClaims:
                const statusDefault = this.enumService
                    .getOptionsFromEnum(ClaimSearchRequest.StatusListEnum)
                    .filter(
                        (status) =>
                            status.value !==
                                ClaimSearchRequest.StatusListEnum.Completed &&
                            status.value !==
                                ClaimSearchRequest.StatusListEnum.Unknown,
                    )
                    .map((status: Option) => {
                        return status.value;
                    });
                form = this.formBuilder.group({
                    activePayers: new FormControl(null),
                    stalledReason: new FormControl(null),
                    claimStatus: new FormControl(statusDefault, {
                        validators: Validators.required,
                    }),
                });
                break;
            default:
                return undefined;
        }
        return form;
    }

    triggerExportData(selectedMetric: Metrics) {
        this.exportResultsSubject.next(selectedMetric);
    }

    getDaysToCompleteDateRangeOptions(): Option[] {
        return [
            {
                label: 'Last 90 days',
                value: DaysToCompleteDateRangeOptions.Last90Days,
            },
            {
                label: 'Last 3 months',
                value: DaysToCompleteDateRangeOptions.Last3Months,
            },
            {
                label: 'Last 6 months',
                value: DaysToCompleteDateRangeOptions.Last6Months,
            },
            {
                label: 'Last 12 months',
                value: DaysToCompleteDateRangeOptions.Last12Months,
            },
            {
                label: 'Custom',
                value: DaysToCompleteDateRangeOptions.Custom,
            },
        ];
    }

    getLocalDateRangeFromDateRangeOptions(form: FormGroup): LocalDateRange {
        const option = form.controls['quarterDateRanges'].getRawValue();

        switch (option) {
            case DaysToCompleteDateRangeOptions.Last90Days:
                return {
                    minDate: dayjs().subtract(91, 'days').format('YYYY-MM-DD'),
                    maxDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                };
            case DaysToCompleteDateRangeOptions.Last3Months:
                return {
                    minDate: dayjs()
                        .startOf('month')
                        .subtract(3, 'months')
                        .format('YYYY-MM-DD'),
                    maxDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                };
            case DaysToCompleteDateRangeOptions.Last6Months:
                return {
                    minDate: dayjs()
                        .startOf('month')
                        .subtract(6, 'months')
                        .format('YYYY-MM-DD'),
                    maxDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                };
            case DaysToCompleteDateRangeOptions.Last12Months:
                return {
                    minDate: dayjs()
                        .startOf('month')
                        .subtract(1, 'year')
                        .format('YYYY-MM-DD'),
                    maxDate: dayjs().startOf('month').format('YYYY-MM-DD'),
                };
            case DaysToCompleteDateRangeOptions.Custom:
                return {
                    minDate: moment(form.controls['startDate'].value)
                        .startOf('day')
                        .format('YYYY-MM-DD'),
                    maxDate: moment(form.controls['endDate'].value)
                        .startOf('day')
                        .format('YYYY-MM-DD'),
                };
            default:
                return undefined;
        }
    }

    getPercent(num: number, total: number): string {
        return Math.round((num / total) * 100).toString() + '%';
    }

    formatCurrency(value: number, useCompact = true) {
        const currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: useCompact ? 'compact' : 'standard',
        });

        return currencyFormatter.format(value);
    }

    getFillColor(colors: string[], index: number) {
        if (isNil(colors) || colors.length < 1) {
            return undefined;
        }

        const colorIndex = index % colors.length;
        return colors[colorIndex];
    }

    getPayerValueForStalledClaims(payerList: any) {
        if (isNil(payerList) || payerList.length < 1 || payerList.includes(0)) {
            return null;
        } else {
            return payerList;
        }
    }
}
